import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Alert, Avatar, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, FormGroup, Link, Paper, Stack, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip,
  Typography,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  PersonAdd as AddIcon,
  DeleteOutline as RemoveIcon,
} from '@mui/icons-material';
import * as orgApi from '../api/organization';
import { useAuth } from '../contexts/AuthContext';

/**
 * Who can administer the whole organisation — every site at once.
 *
 * Distinct from a site's own Administrators screen, which grants authority over one
 * clinic. The warning about a lone administrator is the same lock-out risk the clinic
 * screen guards against, one level up.
 */
export default function OrgAdmins() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [sites, setSites] = useState([]);
  // Choosing where an admin also practises: { admin, picked: Set<clinicId> }
  const [practise, setPractise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ email: '', full_name: '' });
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await orgApi.listOrgAdmins();
      setAdmins(data.admins || []);
      setSites(data.sites || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load administrators');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSites = async (row, ids) => {
    setBusyId(row.id);
    try {
      const res = await orgApi.setOrgAdminProviderSites(row.id, ids);
      toast.success(res.message || 'Updated');
      setPractise(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update where they practise');
    } finally {
      setBusyId(null);
    }
  };

  const toggleProvider = (row, on) => {
    if (!on) { saveSites(row, []); return; }
    // Turning it on asks where: every site, unless they narrow it.
    setPractise({ admin: row, picked: new Set(sites.map((s) => s.id)) });
  };
  const siteName = Object.fromEntries(sites.map((s) => [s.id, s.name]));

  const invite = async () => {
    if (!form.email.trim()) { toast.error('An email address is required'); return; }
    setSaving(true);
    try {
      const res = await orgApi.addOrgAdmin({
        email: form.email.trim(),
        full_name: form.full_name.trim() || null,
      });
      toast.success(res.message || 'Invitation sent');
      setInviteOpen(false);
      setForm({ email: '', full_name: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add that administrator');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (admin) => {
    setBusyId(admin.id);
    try {
      await orgApi.removeOrgAdmin(admin.id);
      toast.success(`${admin.full_name || admin.email} is no longer an organisation admin`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove them');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}><AdminIcon /></Avatar>
        <Box>
          <Typography variant="h4" fontWeight={700}>Organisation administrators</Typography>
          <Typography variant="body2" color="text.secondary">
            Who can manage every site in {user?.org_name || 'your organisation'}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ ml: 'auto' }}
          onClick={() => setInviteOpen(true)}>
          Add administrator
        </Button>
      </Box>

      {!loading && admins.length === 1 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You're the only organisation administrator. Adding a second one means nobody
          gets locked out if you lose access to your account.
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        An organisation administrator can manage every site here, including ones added
        later. To give somebody authority over a single clinic instead, use that site's
        own Administrators screen.
      </Alert>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {admins.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell>
                    {/* component="div": a Chip renders a <div>, and Typography defaults
                        to <p>, which cannot legally contain one. */}
                    <Typography component="div" fontWeight={600}>
                      {a.full_name || '—'}
                      {a.is_you && <Chip label="You" size="small" sx={{ ml: 1 }} />}
                    </Typography>
                  </TableCell>
                  <TableCell>{a.email}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {a.pending_invite && <Chip label="Invite pending" size="small" color="warning" />}
                      {!a.is_active && <Chip label="Deactivated" size="small" />}
                      {a.is_active && !a.pending_invite && (
                        <Chip label="Active" size="small" color="success" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Tooltip title={a.provider_at?.length
                        ? 'Stop practising — they stay an organisation administrator'
                        : 'Let them see patients and run sessions as a provider at sites here'}>
                        <span>
                          <Switch size="small" color="secondary"
                            checked={Boolean(a.provider_at?.length)}
                            disabled={busyId === a.id || !sites.length}
                            onChange={(e) => toggleProvider(a, e.target.checked)} />
                        </span>
                      </Tooltip>
                      {a.provider_at?.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
                            {a.provider_at.length === sites.length
                              ? 'All sites'
                              : a.provider_at.map((id) => siteName[id]).filter(Boolean).join(', ')}
                          </Typography>
                          <Link component="button" variant="caption" underline="hover"
                            onClick={() => setPractise({ admin: a, picked: new Set(a.provider_at) })}>
                            Change sites
                          </Link>
                        </Box>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={
                      a.is_you
                        ? "You can't remove your own access"
                        : admins.length <= 1
                          ? 'The only administrator cannot be removed'
                          : 'Remove organisation access'
                    }>
                      <span>
                        <Button
                          size="small" color="error" startIcon={<RemoveIcon />}
                          disabled={a.is_you || admins.length <= 1 || busyId === a.id}
                          onClick={() => remove(a)}
                        >
                          Remove
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(practise)} onClose={() => busyId == null && setPractise(null)}
        maxWidth="xs" fullWidth>
        <DialogTitle>Where does {practise?.admin.full_name || practise?.admin.email} practise?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            At the sites you tick they can see patients, run scribe sessions and write notes
            as a provider, and patients can book them. They stay an organisation
            administrator either way.
          </Typography>
          <FormGroup>
            {sites.map((s) => (
              <FormControlLabel key={s.id} label={s.name}
                control={(
                  <Checkbox checked={practise?.picked.has(s.id) || false}
                    onChange={(e) => {
                      const next = new Set(practise.picked);
                      if (e.target.checked) next.add(s.id); else next.delete(s.id);
                      setPractise({ ...practise, picked: next });
                    }} />
                )} />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPractise(null)} disabled={busyId != null}>Cancel</Button>
          <Button variant="contained" disabled={busyId != null}
            onClick={() => saveSites(practise.admin, [...practise.picked])}>
            {practise?.picked.size ? 'Save' : 'Remove provider role'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add an organisation administrator</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Email" required fullWidth value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <TextField label="Full name" fullWidth value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            <Alert severity="info">
              If this address already has a ScribeLive account, the access is added to it
              — they keep one password. Otherwise they get a single-use link, valid 48
              hours, and set their own.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={invite} disabled={saving}>
            {saving ? 'Sending…' : 'Send invitation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
